import { animate, style, transition, trigger } from '@angular/animations';
import {
  CdkConnectedOverlay,
  CdkOverlayOrigin,
  ConnectedPosition,
  FlexibleConnectedPositionStrategyOrigin,
  Overlay,
  OverlayRef,
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  TemplateRef,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogRef } from '@angular/material/dialog';
import { BadgeComponent } from '@shared/ui/badge';
import { ButtonComponent } from '@shared/ui/button';
import { ModalsService } from '@shared/ui/modals';
import { NgScrollbar } from 'ngx-scrollbar';
import {
  MenuModalActionInterface,
  MenuModalButtonTypeEnum,
  MenuModalDataInterface,
} from './modal';
import { MenuModalComponent } from './modal/menu-modal.component';
import { of } from 'rxjs';

const constants = {
  DROPDOWN_ANIMATION_DURATION_MS: 150,
};

@Component({
  selector: 'shared-ui-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.css'],
  imports: [
    NgScrollbar,
    ButtonComponent,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    NgTemplateOutlet,
    BadgeComponent,
  ],
  animations: [
    trigger('openClose', [
      transition('void => false', [
        style({
          opacity: 0,
          'margin-top': -5,
        }),
        animate(
          constants.DROPDOWN_ANIMATION_DURATION_MS,
          style({
            opacity: 1,
            'margin-top': 2,
          }),
        ),
      ]),
      transition('false => void', [
        animate(
          constants.DROPDOWN_ANIMATION_DURATION_MS,
          style({
            opacity: 0,
            'margin-top': -5,
          }),
        ),
      ]),
    ]),
  ],
})
export class MenuComponent implements OnDestroy {
  private readonly modalsService = inject(ModalsService);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly overlay = inject(Overlay);

  title = input<string>();

  extraTitle = input<string | number>();

  height = input<number>();

  maxHeight = input<number>(400);

  isDisabled = input<boolean>(false);

  isMobile = input(false);

  isInline = input(false);

  isRadioInline = input(false);

  hidden = input(false);

  closeAfterClick = input(true);

  buttonClass = input<string>();

  menuClass = input<string>();

  dialogActions = input<MenuModalActionInterface[]>();

  dialogTitle = input<string>();

  dialogDisableActions = input<boolean>();

  placeholder = input<string>('');

  dialogClose = output<boolean>();

  dialogConfirm = output<void | string | number | (string | number)[]>();

  protected isOpen = signal(false);

  private readonly menu = viewChild.required<TemplateRef<any>>('menu');

  private readonly content = viewChild.required<TemplateRef<any>>('content');

  private readonly header = viewChild.required<TemplateRef<any>>('header');

  private readonly footer = viewChild.required<TemplateRef<any>>('footer');

  private readonly dialogConfig = computed(() => ({
    title: this.dialogTitle() ?? this.title() ?? '',
    menuContent: this.content(),
    menuHeader: this.header(),
    menuFooter: this.footer(),
    closeAfterClick: this.closeAfterClick(),
    disableActions: this.dialogDisableActions(),
    actions: this.dialogActions() ?? [
      {
        title: $localize`:@@menuDefaultCloseButton:Cancel`,
        type: MenuModalButtonTypeEnum.SECONDARY,
        closeModal: true,
      },
      {
        title: $localize`:@@menuDefaultConfirmButton:Confirm`,
        type: MenuModalButtonTypeEnum.PRIMARY,
        focus: true,
        closeModal: true,
        action: () => {
          this.dialogConfirm.emit();
          return of(true);
        },
      },
    ],
  }));

  private readonly dialogAfterCloseActions = computed(() =>
    this.dialogConfig().actions.filter((action) => action.closeModal),
  );

  private overlayRef: OverlayRef | null | undefined;

  private readonly destroyRef = inject(DestroyRef);

  ngOnDestroy(): void {
    this.close();
  }

  open(
    connectionStrategyOrigin?: FlexibleConnectedPositionStrategyOrigin,
    connectedPosition?: ConnectedPosition,
    title?: string,
  ): void {
    if (this.isMobile()) {
      this.openModal(title)
        .beforeClosed()
        .subscribe((value) => {
          this.dialogAfterCloseActions().forEach((action) => action.action?.());
          this.dialogClose.emit(!!value);
        });
    } else if (connectionStrategyOrigin) {
      this.close();

      const positionStrategy = this.overlay
        .position()
        .flexibleConnectedTo(connectionStrategyOrigin)
        .withPositions([
          connectedPosition ?? {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
          },
        ]);

      this.overlayRef = this.overlay.create({
        hasBackdrop: true,
        backdropClass: 'cdk-overlay-transparent-backdrop',
        positionStrategy,
      });

      this.overlayRef.attach(
        new TemplatePortal(this.menu(), this.viewContainerRef),
      );

      this.overlayRef
        .backdropClick()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(($event) => {
          this.close();
        });
    } else {
      this.isOpen.set(true);
    }
  }

  protected onContentClick(): void {
    if (this.closeAfterClick()) {
      this.close();
    }
  }

  protected toggle(): void {
    if (this.isMobile()) {
      this.openModal()
        .afterClosed()
        .subscribe((value) => this.dialogClose.emit(!!value));
    } else {
      this.isOpen.update((value) => !value);
    }
  }

  protected close(): void {
    this.isOpen.set(false);

    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  private openModal(
    title?: string,
  ): MatDialogRef<MenuModalComponent, boolean | undefined> {
    return this.modalsService.open<
      MenuModalComponent,
      MenuModalDataInterface,
      boolean | undefined
    >(MenuModalComponent, {
      data: {
        ...this.dialogConfig(),
        title: title ?? this.dialogConfig().title,
      },
    });
  }
}
